import { IconDefinition } from '@fortawesome/pro-duotone-svg-icons';
import { faLink } from '@fortawesome/pro-duotone-svg-icons';
import { faCircle1 } from '@fortawesome/pro-solid-svg-icons';
import { faCircle2 } from '@fortawesome/pro-solid-svg-icons';
import { faCircle3 } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { tss } from 'tss-react';

import ProductTableItemButtons from 'components/desktop/Recipe/RecipeTabs/Components/RecipeErpViewNew/Detail/ProductTableItemButtons';
import { RecipeErpViewComponentProps } from 'components/desktop/Recipe/RecipeTabs/Components/RecipeErpViewNew/Index';
import * as State from 'components/desktop/Recipe/RecipeTabs/Components/RecipeErpViewNew/State';
import { RenderIf } from 'components/desktop/_general/Conditional/RenderIf';
import { ImageWithFallback } from 'components/desktop/_general/ImageWithFallback/ImageWithFallback';
import { getPriceString } from 'functions/numberToString';
import useCultureCode from 'hooks/redux/useCultureCode';
import useCurrency from 'hooks/redux/useCurrency';
import * as Product from 'types/Recipe/Detail/ErpNew/Product';
import * as ErpSourceList from 'types/_general/Store/ErpSourceList';
import Style from 'types/_general/Style';

export const ProductTableItem = ({
	product,
	...props
}: RecipeErpViewComponentProps & {
	product: Product.Type;
	ingredientId: string;
	segmentIndex: number;
}) => {
	const { classes } = useStyles({
		isSelected: Boolean(product.selected),
	});
	const currency: string = useCurrency();
	const cultureCode = useCultureCode();

	return (
		<tr className={product.selected ? 'table-primary' : undefined}>
			<td className={classes.firstColumn}>
				<ProductTableItemButtons
					product={product}
					toggleSelection={() =>
						props.handlers.toggleProductSelection({
							ingredientId: props.ingredientId,
							productId: product.productId,
							segmentIndex: props.segmentIndex,
						})
					}
					isInComparisonLeft={State.isInComparison(props.state, {
						ingredientId: props.ingredientId,
						productId: product.productId,
						position: 'left',
					})}
					isInComparisonRight={State.isInComparison(props.state, {
						ingredientId: props.ingredientId,
						productId: product.productId,
						position: 'right',
					})}
					addToComparisonLeft={() =>
						props.handlers.toggleComparison({
							ingredientId: props.ingredientId,
							productId: product.productId,
							position: 'left',
						})
					}
					addToComparisonRight={() =>
						props.handlers.toggleComparison({
							ingredientId: props.ingredientId,
							productId: product.productId,
							position: 'right',
						})
					}
				/>
			</td>

			<td className={classes.secondColumn}>
				<ImageWithFallback
					src={product.imageUrl}
					className="image-with-fallback"
					style={{
						...style.image,
						objectFit: 'contain',
					}}
					onClick={() => visitSourcePage(product)}
				/>
			</td>

			<td>
				<span
					style={{
						marginRight: product.externalUrl ? '5px' : '0',
					}}
				>
					{ErpSourceList.getNameById(props.data.erpSources, product.sourceId)}
				</span>
				<RenderIf condition={Boolean(product.externalUrl)}>
					<FontAwesomeIcon
						icon={faLink}
						size={'xs'}
						onClick={() => visitSourcePage(product)}
						style={{ color: 'grey' }}
					/>
				</RenderIf>
			</td>

			<td>
				{renderPriority(product.priority ?? 0)}
				{product.name}
			</td>

			<td>{`${
				product.price ? getPriceString(product.price, cultureCode) : '–'
			} ${currency}`}</td>
			<td>{product.gtin}</td>
			{props.state.selectedColumns.map((nutrientId, i) => {
				return (
					<td style={{ width: '150px', minWidth: '150px' }} key={i}>
						<div>{Product.getNutrientValueString(product, nutrientId)}</div>
					</td>
				);
			})}
		</tr>
	);
};

const useStyles = tss.withParams<{ isSelected: boolean }>().create(({ isSelected }) => ({
	firstColumn: {
		width: '65px',
		minWidth: '65px',
	},
	secondColumn: {
		width: '50px',
		minWidth: '50px',
	},
	container: {
		backgroundColor: isSelected ? '#d3f2c2' : undefined,
		cursor: 'pointer',
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'center',
	},
	imageName: {
		display: 'flex',
		alignItems: 'center',
		gap: '5px',
	},
	image: {
		height: '30px',
		width: '30px',
		objectFit: 'cover',
		marginRight: '5px',
	},
}));

export function getPriorityColor(priority: number): string {
	if (priority === 1) return '#edb71f';
	if (priority === 2) return '#a8a8aa';
	return '#e88949';
}

export function getIcon(priority: number): IconDefinition {
	if (priority === 1) return faCircle1;
	if (priority === 2) return faCircle2;
	return faCircle3;
}

export function renderPriority(priority: number): JSX.Element {
	if (![1, 2, 3].includes(priority)) return <></>;
	return (
		<FontAwesomeIcon
			title={'Priority ' + priority}
			size={'lg'}
			style={{ color: getPriorityColor(priority), marginRight: '5px' }}
			icon={getIcon(priority)}
		/>
	);
}

export function visitSourcePage(product: Pick<Product.Type, 'externalUrl'> | null) {
	if (!product) return;
	if (!product.externalUrl) return;
	window.open(product.externalUrl, '_blank');
}

const style: Style = {
	image: {
		width: '59px',
		height: '59px',
		marginLeft: '-kpx',
		marginRight: '8px',
		border: '1px solid #ccc',
		objectFit: 'contain',
	},
};
